import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, Bug, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class PanelErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[PanelErrorBoundary]", error, info);
  }

  private handleReport = () => {
    const err = this.state.error;
    window.dispatchEvent(
      new CustomEvent("panel-help:report-bug", {
        detail: {
          message: err?.message ?? "Nieznany błąd renderowania",
          stack: err?.stack ?? null,
          source: "render",
          path: window.location.pathname,
        },
      }),
    );
  };

  private handleReload = () => {
    this.setState({ error: null });
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <div className="max-w-xl mx-auto my-8 rounded-2xl border border-destructive/30 bg-destructive/5 p-6 sm:p-8 space-y-4">
        <div className="flex items-start gap-3">
          <span className="w-10 h-10 rounded-full bg-destructive/10 text-destructive flex items-center justify-center shrink-0">
            <AlertTriangle className="w-5 h-5" />
          </span>
          <div className="space-y-1">
            <h2 className="text-base font-semibold">Coś poszło nie tak</h2>
            <p className="text-sm text-muted-foreground">
              W panelu wystąpił nieoczekiwany błąd. Pomóż nam go naprawić - zgłoś problem
              i jeśli możesz, dołącz zrzut ekranu.
            </p>
          </div>
        </div>

        <div className="rounded-lg bg-background border border-border p-3 text-xs font-mono text-muted-foreground break-words">
          {this.state.error.message || "Brak szczegółów błędu"}
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button type="button" onClick={this.handleReport} className="flex-1">
            <Bug className="w-4 h-4 mr-2" />
            Zgłoś błąd (pomóż nam ulepszać stronę)
          </Button>
          <Button type="button" variant="outline" onClick={this.handleReload} className="sm:w-auto">
            <RefreshCw className="w-4 h-4 mr-2" />
            Odśwież
          </Button>
        </div>
      </div>
    );
  }
}

export default PanelErrorBoundary;
